import React, { useState, useEffect, useMemo } from 'react';
import TransactionTabs from '../../modules/transactions/TransactionTabs';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useInvoices } from '../../hooks/useInvoices';
import { useContracts } from '../../hooks/useContracts';
import { useBills } from '../../hooks/useBills';
import { useBranches } from '../../hooks/useBranches';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import { ErrorState, StatsCards, PageHeader } from '../../components/Common';

const Dashboard: React.FC = () => {
  const [selectedCurrency, setSelectedCurrency] = useState<string>('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    console.log('🚀 Dashboard component mounted');
    setMounted(true);
    return () => console.log('👋 Dashboard component unmounted');
  }, []);

  const { data: currencies = [], isLoading: currenciesLoading, error: currenciesError } = useCurrencies({ populate: '*' });
  const { data: invoices = [], isLoading: invoicesLoading, error: invoicesError } = useInvoices({ populate: '*' });
  const { data: contracts = [], isLoading: contractsLoading, error: contractsError } = useContracts({ populate: '*' });
  const { data: bills = [], isLoading: billsLoading, error: billsError } = useBills({ populate: '*' });
  const { data: branches = [], isLoading: branchesLoading, error: branchesError } = useBranches({ populate: '*' });

  useEffect(() => {
    console.log('📊 Dashboard API Data:', {
      mounted, currencies: currencies?.length || 0, invoices: invoices?.length || 0,
      contracts: contracts?.length || 0, bills: bills?.length || 0, branches: branches?.length || 0,
      errors: { currencies: currenciesError?.message, invoices: invoicesError?.message, contracts: contractsError?.message, bills: billsError?.message, branches: branchesError?.message },
    });
  }, [mounted, currencies, invoices, contracts, bills, branches, currenciesError, invoicesError, contractsError, billsError, branchesError]);

  useEffect(() => {
    if (currencies && currencies.length > 0 && !selectedCurrency) {
      const savedCurrency = localStorage.getItem('selectedCurrency');
      if (savedCurrency) {
        const exists = currencies.some((c: any) => c.code === savedCurrency);
        if (exists) { setSelectedCurrency(savedCurrency); return; }
      }
      const firstCurrency = currencies[0];
      if (firstCurrency?.code) setSelectedCurrency(firstCurrency.code);
    }
  }, [currencies, selectedCurrency]);

  const exchangeRates = useMemo(() => {
    const rates: Record<string, number> = {};
    if (currencies && Array.isArray(currencies) && currencies.length > 0) {
      currencies.forEach((currency: any) => {
        const code = currency.code;
        const rate = currency.exchangeRateToEGP || currency.rate || 1;
        if (code) rates[code] = rate;
      });
    }
    return rates;
  }, [currencies]);

  const kpiData = useMemo(() => {
    let totalRevenue = 0, invoiceCount = 0;
    if (invoices && Array.isArray(invoices) && invoices.length > 0) {
      invoiceCount = invoices.length;
      invoices.forEach((invoice: any) => {
        const total = parseFloat(invoice.total) || parseFloat(invoice.amount) || 0;
        const currCode = resolveCurrencyCode(invoice.currency, currencies as any[]);
        const rate = currCode && exchangeRates[currCode] ? exchangeRates[currCode] : 1;
        totalRevenue += total * rate;
      });
    }

    let activeContracts = 0, totalContracts = 0;
    if (contracts && Array.isArray(contracts) && contracts.length > 0) {
      totalContracts = contracts.length;
      activeContracts = contracts.filter((c: any) => c.status === 'active' || c.status === 'Active').length;
    }

    let fleetUtilization = 0, totalVessels = 0, activeVessels = 0;
    if (branches && Array.isArray(branches) && branches.length > 0) {
      const vessels = branches.filter((b: any) => b.type === 'vessel' || b.type === 'Vessel' || b.branchType === 'vessel');
      totalVessels = vessels.length;
      activeVessels = vessels.filter((v: any) => v.status === 'active' || v.status === 'Active').length;
      fleetUtilization = totalVessels > 0 ? Math.round((activeVessels / totalVessels) * 100) : 0;
    }

    let monthlyExpenses = 0, billCount = 0;
    if (bills && Array.isArray(bills) && bills.length > 0) {
      const now = new Date();
      bills.forEach((bill: any) => {
        const dueDate = bill.dueDate || bill.createdAt || bill.date;
        if (dueDate) {
          const billDate = new Date(dueDate);
          if (billDate.getMonth() === now.getMonth() && billDate.getFullYear() === now.getFullYear()) {
            const amount = parseFloat(bill.amount) || parseFloat(bill.total) || 0;
            const billCurrCode = resolveCurrencyCode(bill.currency, currencies as any[]);
            const rate = billCurrCode && exchangeRates[billCurrCode] ? exchangeRates[billCurrCode] : 1;
            monthlyExpenses += amount * rate;
            billCount++;
          }
        }
      });
    }

    return { totalRevenue, invoiceCount, activeContracts, totalContracts, fleetUtilization, totalVessels, activeVessels, monthlyExpenses, billCount, netBalance: totalRevenue - monthlyExpenses };
  }, [invoices, contracts, branches, bills, exchangeRates, currencies]);

  const formatCurrency = (amount: number, currencyCode?: string) => {
    const currencyData = currencies.find((c: any) => c.code === currencyCode);
    const symbol = currencyData?.symbol || currencyCode || '';
    if (amount >= 1000000) return `${symbol}${(amount / 1000000).toFixed(2)}M`;
    if (amount >= 1000) return `${symbol}${(amount / 1000).toFixed(1)}K`;
    return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const currency = e.target.value;
    setSelectedCurrency(currency);
    localStorage.setItem('selectedCurrency', currency);
  };

  const currencyOptions = useMemo(() => {
    if (!currencies || !Array.isArray(currencies) || currencies.length === 0) return [];
    return currencies
      .filter((currency: any) => currency.isActive !== false)
      .map((currency: any) => ({ value: currency.documentId || currency.id || '', label: `${currency.code || ''} - ${currency.name || currency.code || 'Unknown'}` }))
      .filter((option: any) => option.value);
  }, [currencies]);

  const isLoading = currenciesLoading || invoicesLoading || contractsLoading || billsLoading || branchesLoading;
  const hasError = currenciesError || invoicesError || contractsError || billsError || branchesError;
  const hasNoCurrencies = !currenciesLoading && currencies.length === 0;

  const convertValue = (baseValue: number): string => {
    if (!selectedCurrency || !exchangeRates[selectedCurrency]) return formatCurrency(baseValue);
    return formatCurrency(baseValue / exchangeRates[selectedCurrency], selectedCurrency);
  };

  const kpis = [
    { title: 'TOTAL REVENUE', value: isLoading ? 'Loading...' : kpiData.totalRevenue > 0 ? convertValue(kpiData.totalRevenue) : '0', subtitle: `${kpiData.invoiceCount} invoices`, changeType: 'positive' as const,
      icon: <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
    { title: 'ACTIVE CONTRACTS', value: isLoading ? 'Loading...' : `${kpiData.activeContracts}`, subtitle: `${kpiData.totalContracts} total contracts`, changeType: 'positive' as const,
      icon: <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> },
    { title: 'FLEET UTILIZATION', value: isLoading ? 'Loading...' : `${kpiData.fleetUtilization}%`, subtitle: `${kpiData.activeVessels}/${kpiData.totalVessels} vessels active`, changeType: 'positive' as const,
      icon: <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg> },
    { title: 'MONTHLY EXPENSES', value: isLoading ? 'Loading...' : kpiData.monthlyExpenses > 0 ? convertValue(kpiData.monthlyExpenses) : '0', subtitle: `${kpiData.billCount} bills this month`, changeType: 'negative' as const,
      icon: <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg> },
  ];

  const dataSummaryStats = [
    { label: 'Currencies', value: currencies?.length || 0, color: 'gray' as const },
    { label: 'Invoices', value: invoices?.length || 0, color: 'blue' as const },
    { label: 'Contracts', value: contracts?.length || 0, color: 'green' as const },
    { label: 'Bills', value: bills?.length || 0, color: 'red' as const },
    { label: 'Branches', value: branches?.length || 0, color: 'purple' as const },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-ocean-600 via-ocean-700 to-ocean-800 rounded-2xl p-8 text-white shadow-2xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-3">Manage your marine logistics operations efficiently</h1>
            <div className="mt-4 flex items-center space-x-2">
              <div className={`h-2 w-2 ${isLoading ? 'bg-yellow-400' : hasError ? 'bg-red-400' : 'bg-green-400'} rounded-full animate-pulse`}></div>
              <span className="text-ocean-100 text-sm">
                {isLoading ? 'Loading data from API...' : hasError ? 'Error loading data' : 'System Online - Live Data'}
              </span>
            </div>
          </div>
          <div className="hidden md:block">
            <svg className="w-20 h-20 text-ocean-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
        </div>
      </div>

      {hasError && <ErrorState message="Some data failed to load. Please check your API connection." entityName="dashboard data" />}

      {/* Currency Selection Section */}
      <div className="card p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            <svg className="w-8 h-8 text-ocean-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Default Currency</h2>
              <p className="text-sm text-gray-600">Select your preferred currency for transactions</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {currenciesLoading ? (
              <div className="text-sm text-gray-500">Loading currencies...</div>
            ) : hasNoCurrencies ? (
              <div className="text-sm text-red-500">No currencies in database. Please add currencies first.</div>
            ) : (
              <>
                <select value={selectedCurrency} onChange={handleCurrencyChange} disabled={currencyOptions.length === 0}
                  className="block w-48 px-4 py-2 text-base border-gray-300 focus:outline-none focus:ring-ocean-500 focus:border-ocean-500 sm:text-sm rounded-md bg-white border">
                  {currencyOptions.length === 0 ? <option value="">No currencies available</option> :
                    currencyOptions.map((option: any) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                {selectedCurrency && exchangeRates[selectedCurrency] && (
                  <div className="text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-md">
                    <span className="font-medium">Exchange Rate:</span> 1 {selectedCurrency} = {exchangeRates[selectedCurrency]?.toFixed(2)} (base rate)
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpis.map((kpi, index) => (
          <div key={index} className="card p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{kpi.title}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{kpi.value}</p>
                <div className="flex items-center mt-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${kpi.changeType === 'positive' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {kpi.subtitle}
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0 p-3 bg-gray-50 rounded-xl">{kpi.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Data Summary */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Summary (Live from API)</h3>
        <StatsCards stats={dataSummaryStats} columns={5} />
      </div>

      {/* Transaction Forms */}
      <div>
        <PageHeader title="Financial Transactions" description="Manage incoming and outgoing transactions">
          <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>Real-time processing</span>
          </div>
        </PageHeader>
        <div className="mt-6">
          <TransactionTabs />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
